import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminAnnouncements = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_active: '',
    announcement_type: '',
    target_audience: '',
    status: '',
    date_from: '',
    date_to: '',
    ...initialFilters
  });

  const activeStatusOptions = [
    { value: '', label: 'All Announcements' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  const announcementStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'expired', label: 'Expired' }
  ];

  const targetAudienceOptions = [
    { value: '', label: 'All Audiences' },
    { value: 'all', label: 'All Users' },
    { value: 'clients', label: 'Clients' },
    { value: 'vendors', label: 'Vendors' },
    { value: 'admins', label: 'Admins' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: announcementsResponse, isLoading, error } = useQuery({
    queryKey: ['adminAnnouncements', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/announcements${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/announcements', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminAnnouncements']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/announcements/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminAnnouncements']);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/announcements/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminAnnouncements']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/announcements/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminAnnouncements']);
    }
  });

  const announcements = useMemo(() => {
    return announcementsResponse?.data?.data || announcementsResponse?.data || [];
  }, [announcementsResponse]);

  const getAnnouncementStatus = (announcement) => {
    const now = new Date();
    const startDate = announcement.start_date ? new Date(announcement.start_date) : null;
    const endDate = announcement.end_date ? new Date(announcement.end_date) : null;

    if (!announcement.is_active) return 'inactive';
    if (startDate && startDate > now) return 'upcoming';
    if (endDate && endDate < now) return 'expired';
    return 'active';
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(announcement => 
      announcement.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      announcement.announcement_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const statistics = useMemo(() => {
    const totalAnnouncements = announcements.length;
    const activeAnnouncements = announcements.filter(a => getAnnouncementStatus(a) === 'active').length;
    const upcomingAnnouncements = announcements.filter(a => getAnnouncementStatus(a) === 'upcoming').length;

    return {
      totalAnnouncements,
      activeAnnouncements,
      upcomingAnnouncements
    };
  }, [announcements]);

  return {
    announcements,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    announcementStatusOptions,
    targetAudienceOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    getAnnouncementStatus,
    createAnnouncement: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateAnnouncement: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    toggleStatus: toggleStatusMutation.mutate,
    isToggling: toggleStatusMutation.isPending,
    deleteAnnouncement: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending
  };
};

